import React, { useRef, useState } from "react";
import { uploadPushBroadcastImage } from "../../api/apiServiceFetch";
import {
	APP_IMAGE_SUMMARY,
	BOTH_IMAGE_SUMMARY,
	PUSH_IMAGE_IOS_WARNING,
	PUSH_IMAGE_SUMMARY,
} from "./broadcastOptions";

/**
 * Картинка рассылки: выбор файла, загрузка и честный рассказ о том, где она
 * появится.
 *
 * Загрузка одна на всё объявление — и на сообщение в чате, и на уведомление.
 * Сервер кладёт файл в публичный бакет и отдаёт готовую ссылку: в чате
 * объявление живёт месяцами, и подписанная на четверть часа ссылка оставила бы
 * от него битую плитку, а картинку уведомления скачивают Expo и служба
 * веб-пушей — им подпись не годится вовсе.
 *
 * Про «где видно» здесь не украшение подписи. В чате картинку видят все, а в
 * уведомлении её нет на iPhone (в сборке приложения нет Notification Service
 * Extension), и админ, проверивший рассылку на своём айфоне, увидел бы
 * уведомление без картинки и решил, что сломана отправка. Поэтому ограничение
 * написано рядом с полем, а после загрузки дублируется тем, что прислал сервер
 * (support).
 */
const BroadcastImagePicker = ({
	imageUrl,
	support,
	disabled,
	onChange,
	withApp,
	withPush,
}) => {
	const inputRef = useRef(null);
	const [fileName, setFileName] = useState("");
	const [isUploading, setIsUploading] = useState(false);
	const [error, setError] = useState(null);

	const summary =
		withApp && withPush
			? BOTH_IMAGE_SUMMARY
			: withApp
				? APP_IMAGE_SUMMARY
				: PUSH_IMAGE_SUMMARY;

	const reset = () => {
		setFileName("");
		setError(null);
		onChange(null);
		if (inputRef.current) inputRef.current.value = "";
	};

	const handleFile = async (event) => {
		const file = event.target.files && event.target.files[0];
		if (!file) return;

		setFileName(file.name);
		setError(null);
		// Прежняя картинка снимается сразу: иначе при неудачной загрузке рассылка
		// уйдёт со старой, а админ будет уверен, что заменил её.
		onChange(null);
		setIsUploading(true);
		try {
			const data = await uploadPushBroadcastImage(file);
			if (!data?.imageUrl) {
				throw new Error("сервер не вернул ссылку на картинку");
			}
			onChange({
				imageUrl: data.imageUrl,
				// Ключ нужен серверу, чтобы убрать файл при отзыве рассылки:
				// без него картинка осталась бы в бакете навсегда.
				imageKey: data.imageKey ?? null,
				support: data.support ?? null,
			});
		} catch (e) {
			console.error("uploadPushBroadcastImage failed:", e);
			setError(
				`Картинку загрузить не удалось: ${e?.message || "запрос не удался"}. Выберите файл ещё раз — без загрузки картинки в рассылке не будет.`,
			);
			if (inputRef.current) inputRef.current.value = "";
		} finally {
			setIsUploading(false);
		}
	};

	const notes = Array.isArray(support?.notes) ? support.notes : [];

	return (
		<div className="broadcasts-page__image">
			<p className="broadcasts-page__step-title" style={{ marginBottom: 4 }}>
				Картинка — по желанию
			</p>
			<p className="broadcasts-page__hint">{summary}</p>

			<div className="broadcasts-page__row">
				<input
					ref={inputRef}
					type="file"
					accept="image/*"
					disabled={disabled || isUploading}
					onChange={handleFile}
				/>
				{imageUrl && (
					<button
						type="button"
						className="broadcasts-page__button--secondary"
						disabled={disabled || isUploading}
						onClick={reset}
					>
						Убрать картинку
					</button>
				)}
			</div>

			{isUploading && (
				<p className="broadcasts-page__hint" style={{ marginTop: 8 }}>
					Загружаем {fileName || "картинку"}...
				</p>
			)}

			{error && <p className="broadcasts-page__status is-error">{error}</p>}

			{imageUrl && !isUploading && (
				<div className="broadcasts-page__image-loaded">
					<img
						className="broadcasts-page__image-preview"
						src={imageUrl}
						alt="Картинка рассылки"
					/>
					<div className="broadcasts-page__image-info">
						<p className="broadcasts-page__status is-ok" style={{ marginTop: 0 }}>
							{withApp
								? "Картинка загружена и уйдёт сообщением в чат."
								: "Картинка загружена и уйдёт вместе с уведомлением."}
						</p>
						{/* Оговорка про iPhone относится только к уведомлению: в чате
						    картинку видно везде, и повторять её там было бы враньём. */}
						{withPush && (
							<p className="broadcasts-page__status is-warn">
								{PUSH_IMAGE_IOS_WARNING}
							</p>
						)}
						{/* support приходит от сервера: если ограничения изменятся
						    (например, появится расширение для iOS), подпись изменится
						    сама, а не останется враньём в вёрстке. */}
						{withPush && notes.length > 0 && (
							<ul className="broadcasts-page__notes">
								{notes.map((note) => (
									<li key={note}>{note}</li>
								))}
							</ul>
						)}
						<p className="broadcasts-page__hint broadcasts-page__hint--beside">
							<a href={imageUrl} target="_blank" rel="noreferrer">
								Открыть файл
							</a>
						</p>
					</div>
				</div>
			)}
		</div>
	);
};

export default BroadcastImagePicker;
